import { IApiSuccessWrapper } from '$/types'
import { apiSlice } from 'app/services/api'
import { IStateAssessment } from 'features/RiskManagement/Assessments'

/**
 * QRA Assessments
 * not in the 'qra' directory because it's not in the API path..
 */
export const qraAssessmentsApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ['QRAAssessment', 'QRASavedState']
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getQRAAssessments: build.query<IStateAssessment[], void>({
        query: () => `/assessments`,
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IStateAssessment[]
        },

        providesTags: (res) => {
          const tags = [{ type: 'QRAAssessment' as const, id: 'LIST' }]

          if (res != null) {
            tags.push(
              ...res.map((state) => ({
                type: 'QRAAssessment' as const,
                id: state.id.toString()
              }))
            )
          }

          return tags
        }
      }),
      getQRAAssessment: build.query<IStateAssessment, string>({
        query: (id) => `/assessments/${id}`,
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IStateAssessment
        },
        providesTags: (_res, _err, id) => [{ type: 'QRAAssessment', id }]
      }),
      addQRAAssessment: build.mutation<
        IStateAssessment,
        Partial<IStateAssessment>
      >({
        query: (assessment) => ({
          url: '/assessments',
          method: 'POST',
          body: assessment
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IStateAssessment
        },
        invalidatesTags: (_res, _err, assessment) => [
          { type: 'QRAAssessment', id: 'LIST' },
          { type: 'QRASavedState', id: 'LIST' },
          { type: 'QRASavedState', id: assessment.savedStateId }
        ]
      }),
      /**
       * Patch action is for scenario rerun.
       * For update, use `PUT` alternative further below
       */
      patchQRAAssessment: build.mutation<
        IStateAssessment,
        Partial<IStateAssessment>
      >({
        query: (assessment) => ({
          url: '/assessments',
          method: 'PATCH',
          body: assessment
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IStateAssessment
        },
        invalidatesTags: (_res, _err, assessment) => [
          { type: 'QRAAssessment', id: 'LIST' },
          { type: 'QRASavedState', id: 'LIST' },
          { type: 'QRASavedState', id: assessment.savedStateId }
        ]
      }),
      updateQRAAssessment: build.mutation<IStateAssessment, IStateAssessment>({
        query: (assessment) => ({
          url: `/assessments/${assessment.id}`,
          method: 'PUT',
          body: assessment
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IStateAssessment
        },
        invalidatesTags: (_res, _err, assessment) => [
          { type: 'QRAAssessment', id: 'LIST' },
          { type: 'QRAAssessment', id: assessment.id },
          { type: 'QRASavedState', id: 'LIST' },
          { type: 'QRASavedState', id: assessment.savedStateId }
        ]
      }),
      deleteQRAAssessment: build.mutation<IStateAssessment, IStateAssessment>({
        query: (assessment) => ({
          url: `/assessments/${assessment.id}`,
          method: 'DELETE'
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IStateAssessment
        },
        invalidatesTags: (_res, _err, assessment) => [
          // NOTE: Only remove reference to assessment instead of assessment itself
          { type: 'QRAAssessment', id: 'LIST' },
          // ~{ type: 'QRAAssessment' as const, id: assessment.id },~
          { type: 'QRASavedState', id: 'LIST' },
          { type: 'QRASavedState', id: assessment.savedStateId }
        ]
      })
    })
  })

export const {
  useGetQRAAssessmentsQuery,
  useGetQRAAssessmentQuery,
  useAddQRAAssessmentMutation,
  usePatchQRAAssessmentMutation,
  useUpdateQRAAssessmentMutation,
  useDeleteQRAAssessmentMutation
} = qraAssessmentsApi
